/**
 * --------------------------------------------------------------------------
 * Popover Close Web Component
 * --------------------------------------------------------------------------
 * Custom element that handles closing popovers/modals.
 * This ensures proper initialization when content is dynamically loaded.
 *
 * Supports both modern popover API and fallback dialog mode.
 *
 * Usage: <popover-close>...</popover-close>
 *
 * Supports attributes:
 * - data-popover-close-target: target specific popover
 * - data-popover-close-all: close all popovers
 * - data-visibility-trigger: "hover" or "click" (default: "click")
 */
if (!customElements.get('popover-close')) {
    class PopoverClose extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            // Ensure it behaves like a button for accessibility
            if (!this.hasAttribute('role')) {
                this.setAttribute('role', 'button');
            }
            if (!this.hasAttribute('tabindex') && this.getAttribute('role') === 'button') {
                this.setAttribute('tabindex', '0');
            }

            // Copy any data attributes from the custom element to ensure they're preserved
            const triggerType = this.getAttribute('data-visibility-trigger');
            const targetId = this.getAttribute('data-popover-close-target');
            const closeAll = this.getAttribute('data-popover-close-all');

            if (triggerType && !this.hasAttribute('data-visibility-trigger')) {
                this.setAttribute('data-visibility-trigger', triggerType);
            }
            if (targetId && !this.hasAttribute('data-popover-close-target')) {
                this.setAttribute('data-popover-close-target', targetId);
            }
            if (closeAll && !this.hasAttribute('data-popover-close-all')) {
                this.setAttribute('data-popover-close-all', closeAll);
            }

            // Add keyboard support
            this.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.click();
                }
            });

            // Set up click handler
            const currentTriggerType = this.getAttribute('data-visibility-trigger') || 'click';
            if (currentTriggerType === 'click') {
                this.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleClose('click');
                });
            }

            // Set up hover handler (if trigger type is hover)
            if (currentTriggerType === 'hover') {
                this.addEventListener('mouseenter', () => {
                    this.handleClose('hover');
                });
            }
        }

        /**
         * Finds the closest dialog element, supporting both popover and fallback modes.
         * @returns {Element|null} The closest dialog/popover element
         */
        findClosestDialog() {
            // Try multiple strategies to find the dialog element

            // Strategy 1: Find element with popover attribute (modern mode)
            let dialog = this.closest('[popover]');

            // Strategy 2: Find dialog-popover component and its dialog
            if (!dialog) {
                const dialogPopover = this.closest('dialog-popover');
                if (dialogPopover && dialogPopover.dialog) {
                    dialog = dialogPopover.dialog;
                }
            }

            // Strategy 3: Find dialog element (fallback mode)
            if (!dialog) {
                dialog = this.closest('dialog');
            }

            // Strategy 4: Find external-popover component and its popover
            if (!dialog) {
                const externalPopover = this.closest('external-popover');
                if (externalPopover && externalPopover.popoverElement) {
                    dialog = externalPopover.popoverElement;
                }
            }

            return dialog;
        }

        /**
         * Closes a dialog element, handling both modern and fallback modes.
         * @param {Element} dialog - The dialog element to close
         * @returns {boolean} True if close was successful
         */
        closeDialogElement(dialog) {
            if (!dialog) return false;

            // Check if this is a dialog-popover component
            const dialogComponent = dialog.closest('dialog-popover');
            if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
                dialogComponent.closeDialog(true); // force close
                return true;
            }

            // Direct close methods
            if (typeof dialog.hidePopover === 'function' && dialog.hasAttribute('popover')) {
                try {
                    dialog.hidePopover();
                    return true;
                } catch {
                    // hidePopover might fail if not in popover state
                }
            }

            if (typeof dialog.close === 'function' && dialog.tagName === 'DIALOG') {
                dialog.close();
                return true;
            }

            return false;
        }

        handleClose(eventType) {
            const triggerType = this.getAttribute('data-visibility-trigger') || 'click';
            const closeAll = this.getAttribute('data-popover-close-all') === 'true' ||
                             this.getAttribute('data-popover-close-all') === '1';
            const targetId = this.getAttribute('data-popover-close-target');

            // Only proceed if the event type matches the trigger type
            if (eventType === 'hover' && triggerType !== 'hover') return;
            if (eventType === 'click' && triggerType !== 'click') return;

            // Determine close options
            const options = {
                force: true, // Always force close to bypass prevent-close
            };

            if (closeAll) {
                options.closeAll = true;
                if (targetId) {
                    options.modalId = targetId; // Close all except this one
                }
            } else if (targetId) {
                options.modalId = targetId; // Close specific modal
            } else {
                // Default: close closest dialog
                const dialog = this.findClosestDialog();

                if (dialog && dialog.id) {
                    options.modalId = dialog.id;
                } else if (dialog) {
                    // If dialog found but no ID, try to close it directly
                    if (this.closeDialogElement(dialog)) {
                        return; // Exit early since we handled it directly
                    }
                    options.closeAll = true; // Fallback to close all if no ID and can't close directly
                } else {
                    options.closeAll = true; // Fallback to close all if no dialog found
                }
            }

            // Close the dialog using the centralized function
            if (typeof window.closeDialogPopover === 'function') {
                window.closeDialogPopover(options);
            } else {
                // Fallback for browsers/contexts where closeDialogPopover might not be available
                const dialog = this.findClosestDialog();
                if (dialog) {
                    this.closeDialogElement(dialog);
                }
            }
        }
    }

    customElements.define('popover-close', PopoverClose);
}
